import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { useGymStore } from "@/stores/gym-store";
import { useGymSchedule } from "@/queries/use-gym-schedules";
import { GymScheduleEditor } from "@/components/gym-schedule-editor";
import { ResourceError } from "@/components/ui/resource-error";
import { Skeleton } from "@/components/ui/skeleton";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { ScrollableContainer } from "@/components/ui/scrollbar";
import { useTranslation } from "react-i18next";
import {
  EVENTABLE_TYPE,
  GYM_SCHEDULE_VIEW_TYPES,
  type GymScheduleViewType,
} from "@/constants/events";

export const Route = createFileRoute(
  "/(protected)/schedules/$scheduleId/edit/",
)({
  loader: () => ({ hasActionButtons: true, actionButtonsCount: 2 }),
  validateSearch: z.object({
    tab: z.enum(GYM_SCHEDULE_VIEW_TYPES).catch(EVENTABLE_TYPE.GroupSession),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { scheduleId } = Route.useParams();
  const { selectedGym } = useGymStore();
  const navigate = Route.useNavigate();
  const { tab } = Route.useSearch();

  const {
    data: schedule,
    isLoading,
    error,
  } = useGymSchedule(selectedGym?.id ?? 0, Number(scheduleId));

  const handleTabChange = (newTab: GymScheduleViewType) => {
    navigate({ search: (prev) => ({ ...prev, tab: newTab }) });
  };

  if (isLoading) {
    return (
      <div className="flex h-full flex-col p-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="mt-4 h-96 w-full flex-1" />
      </div>
    );
  }

  if (error) {
    return <ResourceError error={error} />;
  }

  if (!schedule) {
    return <ResourceError />;
  }

  return (
    <ScrollableContainer>
      <div className="flex min-h-full flex-col">
        <MobilePageTitle className="mt-5 mb-4 px-4">
          {t("pages:scheduling.gymScheduleEditor.title")}
        </MobilePageTitle>
        <div className="flex min-w-[309px] flex-1 flex-col px-4 pb-4">
          <GymScheduleEditor
            schedule={schedule}
            activeTab={tab}
            onTabChange={handleTabChange}
          />
        </div>
      </div>
    </ScrollableContainer>
  );
}
